import React from 'react';
import { BreadcrumbSchema, FAQSchema, ArticleSchema } from '@/components/JsonLd';
import { RichHero, TableOfContents, ProseSection, AnswerBox, FactGrid, RichFAQ, RelatedLinks, RichCTA } from '../RichBlocks';
import RichPriceComparison from '../RichPriceComparison';
import RichEvBenefits from '../RichEvBenefits';
import SEODisclaimer from '@/components/seo/content/SEODisclaimer';

/**
 * BestListRich — bespoke template for best_list pages ("Top 10 elselskaber").
 * Hard-coded, factual buying-guide content + the real market comparison.
 */
export default function BestListRich({ page }) {
  const geo = page?.geography || {};
  const name = geo.name;
  const place = name ? ` i ${name}` : '';
  const zone = geo.zone || 'DK1';
  const year = page?.year || 2026;
  const p = page?.pricing_data || {};
  const enkelCost = p?.enkel?.yearlyCost || 216;
  const consumption = p?.consumption || 9000;
  const totalCompanies = p?.totalCompanies || 54;
  const updated = new Date(page?.last_data_refresh || page?.updated_date || Date.now())
    .toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });

  const faqs = (page?.faqs?.length ? page.faqs : [
    { question: 'Hvem er det billigste elselskab?', answer: 'enkel energi er dokumenteret blandt de billigste: spotpris + 0 øre tillæg fra 18 kr./md. Kåret billigst af NicolasKJ.dk og bedste ladeløsning af ELBIIL.dk.' },
    { question: 'Hvordan vælger jeg det bedste elselskab?', answer: 'Sammenlign tre ting: tillæg pr. kWh, abonnement pr. måned og hvilke services der er inkluderet (Saving Mode, serviceaftale, laderabat). Den fysiske strøm er identisk uanset leverandør.' },
    { question: 'Er billige elselskaber pålidelige?', answer: 'Ja. Alle danske elselskaber er reguleret af Forsyningstilsynet, og strømmen leveres altid af dit lokale netselskab – uanset hvilket selskab du vælger.' },
  ]);

  const toc = [
    { id: 'svar', label: 'Kort svar' },
    { id: 'kriterier', label: 'Hvad gør et selskab bedst?' },
    { id: 'priser', label: 'Prissammenligning' },
    { id: 'elbil', label: 'For elbilister' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <article itemScope itemType="https://schema.org/Article">
      <BreadcrumbSchema items={[{ name: 'Forside', path: '/' }, { name: 'Sammenlign elselskaber', path: '/elpris-sammenligning' }, { name: page?.h1 || 'Bedste elselskaber', path: `/sammenlign/${page.slug}` }]} />
      <FAQSchema faqs={faqs} />
      <ArticleSchema headline={page?.h1} description={page?.meta_description} datePublished={page?.created_date} dateModified={page?.last_data_refresh || page?.updated_date} />

      <RichHero
        eyebrow={`Guide · Elselskaber · ${year}`}
        h1={page?.h1 || `Bedste elselskaber${place} ${year}`}
        intro={page?.intro_text || `Sådan finder du det bedste og billigste elselskab${place} i ${year} – uden skjulte gebyrer og binding.`}
        badges={['Uvildig prisdata', '0 øre tillæg hos enkel', 'Bedst i test']}
        updated={updated}
        image="https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1600&q=70"
        kpis={[
          { value: `${enkelCost.toLocaleString('da-DK')} kr./år`, label: `enkel ved ${consumption.toLocaleString('da-DK')} kWh` },
          { value: 'Nr. 1', label: `af ${totalCompanies}+ selskaber` },
          { value: '3 i top 10', label: 'i stor forbrugertest' },
          { value: '0 kr.', label: 'at skifte' },
        ]}
      />
      <TableOfContents items={toc} />

      <ProseSection id="svar" heading="Hvilket elselskab er bedst – kort svar">
        <AnswerBox question="Hvad kendetegner det bedste elselskab?">
          Det bedste elselskab har <strong>lavt eller intet tillæg</strong> til spotprisen, et <strong>lavt abonnement</strong> og <strong>inkluderede services</strong> uden binding. enkel energi leverer alle tre: 0 øre tillæg, abonnement fra 18 kr./md., og Saving Mode + serviceaftale + laderabat til 0 kr./md.
        </AnswerBox>
      </ProseSection>

      <ProseSection id="kriterier" heading="Hvad gør et elselskab bedst?" alt>
        <p>Da den fysiske strøm er identisk uanset leverandør, handler valget reelt om vilkårene oveni. Kig efter:</p>
        <FactGrid columns={2} facts={[
          { value: 'Tillæg', label: 'Lavt eller 0 øre/kWh', sub: 'enkel: 0 øre' },
          { value: 'Abonnement', label: 'Lav fast pris', sub: 'enkel: fra 18 kr./md.' },
          { value: 'Binding', label: 'Ingen binding er bedst', sub: 'enkel: ingen binding' },
          { value: 'Services', label: 'Saving Mode, laderabat, service', sub: 'enkel: inkluderet' },
        ]} />
      </ProseSection>

      <RichPriceComparison
        enkelCost={enkelCost}
        consumption={consumption}
        totalCompanies={totalCompanies}
        topCompetitors={p?.topCompetitors}
        timestamp={p?.timestamp}
        place={place}
      />

      <RichEvBenefits evData={p?.evData} />

      <section className="py-12 bg-[#F6F3EC]">
        <div className="container mx-auto px-6">
          <h2 id="faq" className="text-2xl md:text-3xl font-bold text-center mb-8 text-[#1F2E33]" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Ofte stillede spørgsmål</h2>
          <RichFAQ faqs={faqs} />
        </div>
      </section>

      <RelatedLinks links={[
        { path: '/elpris-sammenligning', label: 'Sammenlign elselskaber' },
        { path: '/elaftale', label: 'Se el-aftaler' },
        { path: '/pris-time-for-time', label: 'Elpris time for time' },
        { path: '/opladere', label: 'Se ladebokse' },
        { path: '/portal/smart-charge', label: 'Sådan virker Saving Mode' },
        { path: '/hvad-koster-el-gennem-enkel', label: 'Hvad koster el hos enkel?' },
      ]} />

      <RichCTA
        headline="Skift til en el-aftale med spotpris uden tillæg"
        subtext="Spotpris uden tillæg fra 18 kr./md., ingen binding, og Saving Mode + service inkluderet."
        primary={{ path: '/elaftale', text: 'Se el-aftaler' }}
        secondary={{ path: '/elpris-sammenligning', text: 'Sammenlign elselskaber' }}
      />
      <SEODisclaimer page={page} />
    </article>
  );
}